// src/utils/fetch.ts
// Fetch unificado con dedupe + cache corto (incluye negative cache) y backoff por endpoint.

type Json = any;

export type ApiLike<T = any> = {
  success?: boolean;
  code?: number | string;
  message?: string;
  data?: T | null;
  unauthorized?: boolean;
  transient?: boolean;
  [k: string]: any;
};

export type RequestOptions = RequestInit & {
  raw?: boolean;       // devuelve Response cruda
  noAuth?: boolean;    // no adjunta token/bearer
  timeoutMs?: number;  // default 15s
};

// ========= Config env / URL =========
declare const __API_BASE__: string | undefined; // inyectado por vite.config → v1
declare const __WEB_BASE__: string | undefined; // inyectado por vite.config → web

const DEV = !!(import.meta as any)?.env?.DEV;
const ABSOLUTE = /^https?:\/\//i;

// ⚠️ SOLO quita api/v1 (api-v1, api_v1, api/v1). NO /api/ a secas (para no romper /api/web).
const API_V1_PREFIX = /^(?:\/)?api(?:[-_]?v1|\/v1)\//i;

const normalizeEndpoint = (endpoint: string) => {
  if (!endpoint) return "";
  if (ABSOLUTE.test(endpoint)) return endpoint.trim();

  // limpiar barras y espacios
  const raw = endpoint.trim().replace(/^\/+/, "");

  // Si ya viene "api/web/..." → NO tocar (se resolverá más abajo con __WEB_BASE__)
  if (/^api\/web\//i.test(raw)) return raw;

  // Quitar SOLO /api/v1 variantes
  let ep = raw.replace(API_V1_PREFIX, "");
  ep = ep.replace(/\/{2,}/g, "/");
  return ep;
};

const pickBase = (kind: "v1" | "web") => {
  const env = (import.meta as any)?.env || {};
  if (kind === "web") {
    if (typeof __WEB_BASE__ !== "undefined" && __WEB_BASE__) {
      return String(__WEB_BASE__).replace(/\/+$/, "");
    }
    // Fallback (dev sin define): usar VITE_WEB_BASE o /api/web
    return String(env.VITE_WEB_BASE || "/api/web").replace(/\/+$/, "");
  } else {
    if (typeof __API_BASE__ !== "undefined" && __API_BASE__) {
      return String(__API_BASE__).replace(/\/+$/, "");
    }
    // Fallback (dev sin define): usar VITE_API_BASE o /api/v1
    return String(env.VITE_API_BASE || "/api/v1").replace(/\/+$/, "");
  }
};

const classifyRoute = (rawEndpoint: string) => {
  // Determina si la ruta debe ir a "web" (1337) o "v1" (8080)
  // Casos WEB:
  //  - empieza con "/api/web/..." o "api/web/..."
  //  - atajo "web/..."
  //  - ofertas-online/* (kebab-case)
  const raw = rawEndpoint.trim().replace(/^\/+/, "");
  if (/^api\/web\//i.test(raw)) return "web";
  if (/^web\//i.test(raw)) return "web";
  if (/^ofertas[-\/]online\//i.test(raw)) return "web";
  // Resto por defecto: v1
  return "v1";
};

const getFullApiUrl = (endpoint: string) => {
  const env = (import.meta as any)?.env || {};
  const raw = endpoint.trim();

  // Si nos pasan absoluta, respetar tal cual
  if (ABSOLUTE.test(raw)) return raw;

  // Normalizamos (sin romper api/web)
  const clean = normalizeEndpoint(raw);
  const which = classifyRoute(raw);

  // 1) Rutas WEB
  if (which === "web") {
    // Si vino prefijada "api/web/..." la limpiamos para no duplicar (dejamos solo el path después de api/web/)
    const pathOnly = raw.replace(/^\/?api\/web\//i, "").replace(/^web\//i, "");
    const base = pickBase("web"); // __WEB_BASE__ (dev=/api/web, prod=abs)
    return `${base}/${pathOnly || clean}`;
  }

  // 2) Rutas v1 especiales: ofertasOnline/* (camelCase) → puedes forzar host con VITE_OFFERS_BASE si existe
  if (/^ofertasOnline/i.test(clean)) {
    const offersBase = (env.VITE_OFFERS_BASE || "").toString().trim().replace(/\/+$/, "");
    if (offersBase) {
      return `${offersBase}/${clean}`;
    }
    const base = pickBase("v1");
    return `${base}/${clean}`;
  }

  // 3) Resto → v1
  {
    const base = pickBase("v1");
    return `${base}/${clean}`;
  }
};

export const apiUrl = (endpoint: string) =>
  (ABSOLUTE.test(endpoint) ? endpoint : getFullApiUrl(endpoint));

// ========= De-dupe GET (sin cache persistente) =========
const inflight = new Map<string, Promise<any>>();

const normalizeForKey = (raw: string) => {
  try {
    const u = new URL(raw, window.location.origin);
    const params = new URLSearchParams(u.search);
    const sorted = new URLSearchParams();
    [...params.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([k, v]) => sorted.append(k, v));
    u.search = sorted.toString();
    u.pathname = u.pathname.replace(/\/{2,}/g, "/");
    return u.toString();
  } catch { return raw; }
};
const k = (m: string, url: string) => `${m}:${normalizeForKey(url)}`;

// ========= per-route backoff / cooldown =========
type FailState = { count: number; last: number };
const fails = new Map<string, FailState>();

const backoffMs = (key: string) => {
  const s = fails.get(key);
  if (!s) return 0;
  // exponencial suave, tope 2s
  return Math.min(2000, 150 * Math.pow(1.8, Math.max(0, s.count - 1)));
};
const markFail = (key: string) => {
  const s = fails.get(key) || { count: 0, last: 0 };
  s.count += 1; s.last = Date.now();
  fails.set(key, s);
};
const clearFail = (key: string) => {
  fails.delete(key);
};

// Debug helper - exponer en window para inspección desde consola
if (typeof window !== 'undefined') {
  (window as any).__debugFetchBackoff = () => {
    console.log('📊 Estado actual de backoff:');
    if (fails.size === 0) {
      console.log('✅ No hay endpoints con fallos registrados');
    } else {
      fails.forEach((state, key) => {
        const nextBackoff = backoffMs(key);
        const lastFailDate = new Date(state.last).toISOString();
        console.log(`  ${key}:`);
        console.log(`    - Fallos: ${state.count}`);
        console.log(`    - Último fallo: ${lastFailDate}`);
        console.log(`    - Próximo backoff: ${nextBackoff}ms`);
      });
    }
    return Array.from(fails.entries()).map(([key, state]) => ({
      key,
      ...state,
      nextBackoff: backoffMs(key)
    }));
  };
  (window as any).__clearFetchBackoff = (key?: string) => {
    if (key) {
      clearFail(key);
      console.log(`🧹 Backoff limpiado para: ${key}`);
    } else {
      fails.clear();
      console.log('🧹 Todos los backoffs limpiados');
    }
  };
}

// ========= Token provider + 401 handler (seteados desde auth-service) =========
let tokenProvider: (() => string | null | undefined) | null = null;
let onUnauthorized: ((where: string) => void) | null = null;
let unauthGuard = 0;

export function setTokenProvider(fn: typeof tokenProvider) { tokenProvider = fn; }
export function setUnauthorizedHandler(fn: typeof onUnauthorized) { onUnauthorized = fn; }

// ========= Core request =========
const DEFAULT_TIMEOUT_MS = Number((import.meta as any)?.env?.VITE_FETCH_TIMEOUT_MS) || 15000;

function deepClone<T>(v: T): T {
  try { return (structuredClone as any)(v); } catch { }
  try { return JSON.parse(JSON.stringify(v)); } catch { }
  return v;
}

async function core<T = any>(endpoint: string, options: RequestOptions = {}): Promise<ApiLike<T> | Response> {
  const method = (options.method || "GET").toUpperCase();
  const url = apiUrl(endpoint);

  const headers = new Headers(options.headers || {});
  headers.set("Accept", "application/json");
  // ayuda a backends que chequean "petición AJAX"
  if (!headers.has("X-Requested-With")) headers.set("X-Requested-With", "XMLHttpRequest");

  // JSON por defecto si hay body (no forzar en FormData)
  const isFD = options.body instanceof FormData;
  if (!isFD && options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  // 👇 MODIFICACIÓN: Token con fallback a localStorage
  if (!options.noAuth) {
    let token: string | null | undefined = null;

    // Primero intentar con tokenProvider (configurado desde auth-service)
    if (tokenProvider) {
      token = tokenProvider();
    }

    // 👇 FALLBACK: Si no hay token, buscar directamente en localStorage
    if (!token) {
      try {
        token = localStorage.getItem("auth_token");
      } catch { }
    }

    // Agregar el token a los headers si existe
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
      headers.set("token", token);
    } else if (DEV) {
      console.debug("[fetch] sin token para", url);
    }
  }

  // credenciales si es cross-origin
  let credentials: RequestCredentials = "same-origin";
  try {
    if (new URL(url, location.href).origin !== location.origin) credentials = "include";
  } catch { }

  // ---- DEDUPE/CACHE para GET JSON ----
  const dedupeKey = k(method, url);

  if (method === "GET" && !options.raw) {
    const running = inflight.get(dedupeKey);
    if (running) return running;
  }

  // backoff por endpoint cuando venimos de 5xx/419 previos
  const delay = backoffMs(dedupeKey);
  if (delay > 0) {
    await new Promise(r => setTimeout(r, delay));
  }

  const controller = new AbortController();
  const to = setTimeout(() => controller.abort(), options.timeoutMs ?? DEFAULT_TIMEOUT_MS);

  const doFetch = () => fetch(url, {
    ...options,
    headers, credentials, mode: "cors", redirect: "follow", signal: controller.signal,
  });

  const exec = (async () => {
    try {
      let res = await doFetch();

      // Reintento corto sólo en 5xx/419 (no 401/404/422/403)
      if (!res.ok && [500, 502, 503, 504, 419].includes(res.status as any)) {
        markFail(dedupeKey);
        await new Promise(r => setTimeout(r, 250));
        res = await doFetch();
      } else {
        clearFail(dedupeKey);
      }

      if (options.raw) return res;

      const ct = res.headers.get("content-type") || "";

      if (!res.ok) {
        // 🔐 403 = sin permisos (NO es sesión expirada)
        if (res.status === 403) {
          if (ct.includes("application/json")) {
            const j = await res.json().catch(() => ({} as any));
            return { success: false, code: 403, forbidden: true, message: j?.message || "No tiene permisos para esta acción", data: null };
          }
          return { success: false, code: 403, forbidden: true, message: "No tiene permisos para esta acción", data: null };
        }

        // 🔐 401/419 = sesión expirada
        if (res.status === 401 || res.status === 419) {
          // 🔐 Evitar bucle de logout si falla el propio endpoint de login
          const isLogin = url.includes("/login");

          if (!isLogin && onUnauthorized && Date.now() - unauthGuard > 500) {
            unauthGuard = Date.now();
            onUnauthorized(url);
          }
          // Intentar extraer JSON — para el endpoint de login puede traer requires_password_change
          if (ct.includes("application/json")) {
            const j = await res.json().catch(() => ({} as any));
            // Si el backend indica que debe cambiar contraseña, propagar como éxito especial
            if (isLogin && (j?.requires_password_change === true || j?.data?.requires_password_change === true)) {
              return { success: true, code: res.status, ...j, data: j?.data ?? j };
            }
            return { success: false, code: res.status, unauthorized: true, message: j?.message || "Sesión expirada", data: null };
          }
          return { success: false, code: res.status, unauthorized: true, message: "Sesión expirada", data: null };
        }

        // No cacheamos errores
        if (res.status === 404) return { success: false, code: 404, message: `404: ${endpoint}`, data: null };
        if (res.status === 422 && ct.includes("application/json")) {
          const j = await res.json().catch(() => ({}));
          return { success: false, code: 422, ...j };
        }
        // 🔐 HTTP 429 - Too Many Requests (login bloqueado)
        if (res.status === 429 && ct.includes("application/json")) {
          const j = await res.json().catch(() => ({}));
          return { success: false, code: 429, blocked: true, blockInfo: j, message: j?.message || "Demasiados intentos de login", data: null };
        }
        if (res.status === 429) {
          return { success: false, code: 429, blocked: true, message: "Cuenta bloqueada temporalmente por seguridad", data: null };
        }
        // Intentar extraer mensaje JSON para errores genéricos (400, etc.)
        if (ct.includes("application/json")) {
          const j = await res.json().catch(() => ({}));
          return { success: false, code: res.status, message: j?.message || `HTTP ${res.status}`, data: j?.data ?? null, errors: j?.errors };
        }
        return { success: false, code: res.status, message: `HTTP ${res.status}` };
      }

      // 204
      if (res.status === 204) {
        const out = { success: true, code: 204, data: null };
        if (method === "GET") { } // No cacheamos 204
        clearFail(dedupeKey);
        return out;
      }

      // JSON
      if (ct.includes("application/json")) {
        const obj = (await res.json().catch(() => ({}))) as Json;
        const out: ApiLike = { success: obj?.success !== false, code: (obj?.code ?? res.status), ...obj };

        // 🔐 Si el backend marca unauthorized en el JSON, propagamos evento
        if ((out as any).unauthorized) {
          if (onUnauthorized && Date.now() - unauthGuard > 500) {
            unauthGuard = Date.now();
            onUnauthorized(url);
          }
        }

        if (method === "GET") { } // No cacheamos JSON
        clearFail(dedupeKey);
        return out;
      }

      // no JSON
      const text = await res.text().catch(() => "");
      if (DEV) console.warn("Respuesta no-JSON:", url, text.slice(0, 120));
      if (method === "GET") { } // No cacheamos fallos no-JSON
      return { success: false, code: res.status, message: "Respuesta inválida" };
    } catch (e: any) {
      if (e?.name === "AbortError") return { success: false, code: -1, message: "Timeout" };
      if (method === "GET") { } // No cacheamos network error
      return { success: false, code: 0, message: e?.message || "Network error" };
    } finally {
      clearTimeout(to);
    }
  })();

  if (method === "GET" && !options.raw) {
    inflight.set(dedupeKey, exec);
    exec.finally(() => inflight.delete(dedupeKey));
  }

  return exec;
}

// ========= API público =========
export const Api = {
  url: apiUrl,

  get: <T = any>(ep: string, h: Record<string, string> = {}) =>
    core<T>(ep, { method: "GET", headers: h }),

  post: <T = any>(ep: string, body?: unknown, h: Record<string, string> = {}) =>
    core<T>(ep, {
      method: "POST",
      headers: h,
      body: body instanceof FormData ? body : JSON.stringify(body ?? {}),
    }),

  put: <T = any>(ep: string, body?: unknown, h: Record<string, string> = {}) =>
    core<T>(ep, {
      method: "PUT",
      headers: h,
      body: body instanceof FormData ? body : JSON.stringify(body ?? {}),
    }),

  patch: <T = any>(ep: string, body?: unknown, h: Record<string, string> = {}) =>
    core<T>(ep, {
      method: "PATCH",
      headers: h,
      body: body instanceof FormData ? body : JSON.stringify(body ?? {}),
    }),

  delete: <T = any>(ep: string, h: Record<string, string> = {}) =>
    core<T>(ep, { method: "DELETE", headers: h }),

  raw: (ep: string, opt: RequestOptions = {}) =>
    core(ep, { ...opt, raw: true }),
};

export default Api;

// utilidad para depurar
export function clearApiCaches() {
  inflight.clear(); fails.clear();
}